'use client';

import { Image } from '@akinon/next/components/image';
import clsx from 'clsx';
import { Link } from '@theme/components';
import Pin from '@theme/views/shoppable-banner/pin';
import { ShoppableListingBannerType } from '@theme/widgets/shoppable-listing-banner/type';

export type ListingBannerPin = {
  sku: string;
  x: number;
  y: number;
  mobileX: number;
  mobileY: number;
};

type ListingBannerProps = {
  image: string;
  alt?: string;
  url?: string;
  pins: ListingBannerPin[];
  isMobile: boolean;
};

export default function ListingBanner({
  image,
  alt,
  url,
  pins,
  isMobile
}: ListingBannerProps) {
  const content = (
    <Image
      src={image}
      alt={alt}
      fill
      aspectRatio={isMobile ? 175 / 330 : 393 / 520}
      sizes={isMobile ? '50vw' : '33vw'}
      imageClassName="object-cover"
    />
  );

  return (
    <div
      data-pin-bounds
      className={clsx(
        'relative h-full min-h-60 overflow-hidden [container-type:size]',
        isMobile ? 'md:hidden' : 'hidden md:block'
      )}
    >
      <div
        className={clsx(
          'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
          isMobile
            ? 'aspect-[175/330] w-[max(100cqw,calc(100cqh*175/330))]'
            : 'aspect-[393/520] w-[max(100cqw,calc(100cqh*393/520))]'
        )}
      >
        {url ? (
          <Link href={url} className="block">
            {content}
          </Link>
        ) : (
          content
        )}
        {pins.map((pin, index) => (
          <Pin key={`${pin.sku}-${index}`} {...pin} variant="card" />
        ))}
      </div>
    </div>
  );
}

export type ListingBannerSlot = ListingBannerProps & {
  key: string;
  productIndex: number;
};

const toNumber = (value: string) => parseFloat(value);

export const getListingBannerSlots = (
  data?: ShoppableListingBannerType
): ListingBannerSlot[] => {
  const pins = (data?.pins ?? [])
    .map(({ value }) => {
      const x = toNumber(value?.x);
      const y = toNumber(value?.y);
      const mobileX = toNumber(value?.mobile_x);
      const mobileY = toNumber(value?.mobile_y);

      return {
        banner: Number(value?.banner) || 1,
        sku: value?.sku?.trim(),
        x,
        y,
        mobileX: isNaN(mobileX) ? x : mobileX,
        mobileY: isNaN(mobileY) ? y : mobileY
      };
    })
    .filter((pin) => pin.sku && !isNaN(pin.x) && !isNaN(pin.y));

  const banners = (data?.banners ?? [])
    .map((banner, index) => {
      const position = Number(banner?.value?.position);
      const mobilePosition = Number(banner?.value?.mobile_position) || position;
      const desktopImage = banner?.kwargs?.value?.desktop_image?.url;

      return {
        index,
        position,
        mobilePosition,
        desktopImage,
        mobileImage: banner?.kwargs?.value?.mobile_image?.url || desktopImage,
        alt: banner?.value?.alt,
        url: banner?.value?.url,
        pins: pins
          .filter((pin) => pin.banner === index + 1)
          .map(({ sku, x, y, mobileX, mobileY }) => ({
            sku,
            x,
            y,
            mobileX,
            mobileY
          }))
      };
    })
    .filter((banner) => banner.position > 0 && banner.desktopImage);

  const toSlots = (isMobile: boolean) =>
    [...banners]
      .sort((a, b) =>
        isMobile ? a.mobilePosition - b.mobilePosition : a.position - b.position
      )
      .map((banner, order) => ({
        key: `${isMobile ? 'mobile' : 'desktop'}-${banner.index}`,
        productIndex:
          (isMobile ? banner.mobilePosition : banner.position) - 1 - order,
        image: isMobile ? banner.mobileImage : banner.desktopImage,
        alt: banner.alt,
        url: banner.url,
        pins: banner.pins,
        isMobile
      }));

  return [...toSlots(false), ...toSlots(true)];
};
